import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { API } from '../App';

// Shared cache so we don't re-fetch for every fighter card
let _cachedStates = null;
let _fetchPromise = null;

function fetchStates() {
  if (_cachedStates) return Promise.resolve(_cachedStates);
  if (_fetchPromise) return _fetchPromise;
  _fetchPromise = API.get('/api/altered-states')
    .then(r => {
      _cachedStates = r.data.states || r.data || [];
      return _cachedStates;
    })
    .catch(() => {
      _cachedStates = [];
      return _cachedStates;
    });
  return _fetchPromise;
}

/**
 * Compact inline substance picker for per-fighter altered state selection.
 * Shows as a small pill/button that opens a dropdown of substance emojis.
 *
 * Props:
 *   selected — slug string or '' for sober
 *   onSelect(slug) — callback
 */
export default function SubstancePickerInline({ selected, onSelect }) {
  const [states, setStates] = useState(_cachedStates || []);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    fetchStates().then(setStates);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const current = states.find(s => s.slug === selected);
  const label = current ? current.emoji : '\u{1F9CA}'; // ice cube emoji for sober

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`px-2 py-1 min-h-[32px] text-sm border transition-colors flex items-center gap-1 ${
          open ? 'border-[#A855F7] bg-[#A855F7]/10' :
          selected ? 'border-[#A855F7]/40 bg-[#A855F7]/5' :
          'border-[#2A2A2A] hover:border-[#A855F7]/30'
        }`}
        title={current ? `${current.name} — ${current.tagline}` : 'Sober (no altered state)'}
        data-testid="substance-picker-inline"
      >
        <span className="text-base leading-none">{label}</span>
        <ChevronDown className="w-3 h-3 text-[#A0A0A0]" />
      </button>
      {open && (
        <div className="absolute z-50 right-0 mt-1 w-48 bg-[#1A1A1A] border border-[#2A2A2A] shadow-xl max-h-52 overflow-y-auto">
          {/* Sober option */}
          <button
            onClick={() => { onSelect(''); setOpen(false); }}
            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs border-b border-[#0F0F0F] transition-colors ${
              !selected ? 'bg-[#A855F7]/10' : 'hover:bg-[#2A2A2A]/50'
            }`}
          >
            <span className="text-base">{'\u{1F9CA}'}</span>
            <div className="min-w-0">
              <span className="text-[#F5F5F5] block">Sober</span>
              <span className="text-[9px] text-[#A0A0A0]">Clear-headed</span>
            </div>
          </button>
          {states.map(s => (
            <button
              key={s.slug}
              onClick={() => { onSelect(s.slug); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs border-b border-[#0F0F0F] transition-colors ${
                selected === s.slug ? 'bg-[#A855F7]/10' : 'hover:bg-[#2A2A2A]/50'
              }`}
            >
              <span className="text-base">{s.emoji}</span>
              <div className="min-w-0">
                <span className="text-[#F5F5F5] block truncate">{s.name}</span>
                <span className="text-[9px] text-[#A0A0A0] block truncate">{s.tagline}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
